import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { AppRole } from "@/lib/permissions";

interface UseUserRoleOptions {
  /** Only run the query when true. Prevents race conditions with session restoration. */
  enabled?: boolean;
}

export function useUserRole(options?: UseUserRoleOptions) {
  const enabled = options?.enabled ?? true;

  return useQuery({
    queryKey: ["user-role"],
    queryFn: async (): Promise<AppRole | null> => {
      const startTime = Date.now();
      console.log('[useUserRole] Starting role fetch...');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.warn('[useUserRole] getUser() returned null — session not ready?');
        // Don't cache null when session isn't ready — throw to trigger retry
        throw new Error('User not available yet — session may still be restoring');
      }

      console.log(`[useUserRole] User found: ${user.id.slice(0, 8)}... Querying role...`);

      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.warn('[useUserRole] Role query failed, will retry:', error.message);
        throw new Error(`Role check error - retrying: ${error.message}`);
      }

      const role = (data?.role as AppRole) ?? null;
      console.log(`[useUserRole] Role resolved: ${role ?? 'none'} (${Date.now() - startTime}ms)`);
      return role;
    },
    enabled,
    retry: 5,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 5 * 60 * 1000,
  });
}
